import React, { useState, useRef, useEffect, useMemo } from "react";
import "../../../css/DesignSystem/Dropdowns/CustomSelect.css";
import { ReactComponent as ArrowDownIcon } from "../../../assets/icons/arrow-down.svg";

const CustomSelect = ({
  options = [],
  value = "",
  selectedLabel = "",
  onChange,
  placeholder = "Select...",
  disabled = false,
  error = false,
  style = {},
  clearable = true,
  searchable = true,
  searchPlaceholder = "Type to filter...",
  label,
  required = false,
  overlay = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = options.find((opt) => opt.id === value);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return options.filter((opt) => {
      if (opt.id === "" || opt.hidden) return false;
      if (!term) return true;

      return String(opt.name || "")
        .toLowerCase()
        .includes(term);
    });
  }, [options, searchTerm]);

  const handleSelect = (opt) => {
    if (disabled || opt.id === "") return;

    onChange?.({ target: { value: opt.id } });
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (disabled) return;

    onChange?.({ target: { value: "" } });
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isOpen, searchable]);

  return (
    <div
      ref={wrapperRef}
      className={`custom-select-wrapper${disabled ? " is-disabled" : ""}${error ? " is-error" : ""}${isOpen ? " is-open" : ""}`}
      style={style}
    >
      {label && (
        <span className="FP_Content align-left">
          {label}
          {required && <span className="fp-form-required">*</span>}
        </span>
      )}

      <div className="custom-select-trigger" onClick={handleToggle}>
        <span
          className={`custom-select-value FP_Content ${!selectedOption && !selectedLabel ? "placeholder" : ""}`}
        >
          {selectedOption?.name || selectedLabel || placeholder}
        </span>
        {overlay && <span className="custom-select-overlay" role="status">Loading…</span>}

        <div className="custom-select-actions">
          {clearable && value && !disabled && (
            <button
              type="button"
              className="custom-select-clear"
              onClick={handleClear}
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
          <ArrowDownIcon />
        </div>
      </div>

      {isOpen && (
        <div className="custom-select-dropdown">
          {searchable && (
            <div
              className="custom-select-search-wrap"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="custom-select-search FP_Content"
              />
            </div>
          )}

          {clearable && value && (
            <div
              className="custom-select-option custom-select-clear-option FP_Content"
              onClick={handleClear}
            >
              Clear selection
            </div>
          )}

          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <div
                key={opt.id}
                className={`custom-select-option FP_Content${opt.id === value ? " selected" : ""}`}
                onClick={() => handleSelect(opt)}
              >
                {opt.name}
              </div>
            ))
          ) : (
            <div className="custom-select-empty FP_Content">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
